import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  Fade,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Business,
  FilterList,
  LocalHospital,
  LocationOn,
  MedicalServices,
  Person,
  Search,
  Videocam,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useState } from "react";
import { isMobile } from "react-device-detect";
import { PrimaryButton } from "../../common";
import { currentFeatures } from "../../../configs/Settings";
import { FormAutoComplete, FormDatePicker } from "../../form";
import { HiddenFilters } from "../SearchFields";

interface IProps {
  control: any;
  watchServiceModel: any;
  watchState: any;
  watchHealthcareService?: any;
  onClearModel: () => void;
  onClearState: () => void;
  onClearSpecialty?: () => void;
  onSubmit: () => void;
  isSearching?: boolean;
  healthcareServiceOptions: any[];
  organizationOptions: any[];
  practitionerOptions: any[];
  healthcareServiceLoading?: boolean;
  hiddenFilters?: HiddenFilters; // Prop para ocultar filtros específicos
}

export function FiltersStep({
  control,
  watchServiceModel,
  watchState,
  watchHealthcareService,
  onClearModel,
  onClearState,
  onClearSpecialty,
  onSubmit,
  isSearching,
  healthcareServiceOptions,
  organizationOptions,
  practitionerOptions,
  healthcareServiceLoading,
  hiddenFilters,
}: IProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [showFilters, setShowFilters] = useState(false);

  const showHealthcareService = currentFeatures?.defaultApp;
  const isVirtual = watchServiceModel?.value === "virtualService";

  return (
    <Fade in={true}>
      <Stack spacing={2}>
        <Paper
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 2,
            display: "flex",
            flexDirection: isMobile || isSmallScreen ? "column" : "row",
            alignItems: isMobile || isSmallScreen ? "stretch" : "center",
            gap: 2,
            border: isSearching ? "1px solid" : "1px solid transparent",
            borderColor: isSearching ? "primary.main" : "transparent",
            transition: "border-color 0.3s",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ minWidth: "fit-content" }}
          >
            <Chip
              icon={isVirtual ? <Videocam /> : <LocalHospital />}
              label={watchServiceModel?.label}
              // Quando o modelo está "travado" (ex.: apenas tele OU apenas presencial),
              // removemos o onDelete para não exibir o botão de limpar.
              onDelete={hiddenFilters?.serviceModel ? undefined : onClearModel}
              color={isVirtual ? "secondary" : "primary"}
              sx={{ fontWeight: "medium" }}
            />

            {!isVirtual && watchState && (
              <Chip
                icon={<LocationOn />}
                label={watchState?.value}
                // Quando há apenas 1 UF possível (ou UF travada pela tela),
                // removemos o onDelete para não exibir o botão de limpar.
                onDelete={hiddenFilters?.state ? undefined : onClearState}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: "medium" }}
              />
            )}

            {watchHealthcareService && (
              <Chip
                icon={<MedicalServices />}
                label={watchHealthcareService?.label || watchHealthcareService?.display}
                onDelete={onClearSpecialty}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: "medium" }}
              />
            )}
          </Stack>

          {!isMobile && !isSmallScreen && (
            <Divider orientation="vertical" flexItem />
          )}

          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ flex: 1 }}
          >
            {/* Campo de especialidade sempre visível para filtro rápido */}
            {showHealthcareService && (
              <Box sx={{ width: isMobile ? "100%" : 250 }}>
                <FormAutoComplete
                  name="healthcareService"
                  control={control}
                  label="Filtrar por especialidade"
                  loading={healthcareServiceLoading}
                  selectOptions={healthcareServiceOptions || []}
                  auxOnChange={() => {
                    // Dispara busca automática quando especialidade é selecionada
                    setTimeout(() => onSubmit(), 300);
                  }}
                />
              </Box>
            )}

            {/* Campo de data - oculto se hiddenFilters?.date for true */}
            {!hiddenFilters?.date && (
              <Box sx={{ width: isMobile ? "100%" : 160 }}>
                <FormDatePicker
                  name="date"
                  control={control}
                  label="Data"
                  minDate={dayjs()}
                />
              </Box>
            )}

            {/* Botão de filtros adicionais - só exibe se houver pelo menos um filtro adicional disponível */}
            {(!hiddenFilters?.clinic || !hiddenFilters?.practitioner) && (
              <Button
                variant={showFilters ? "contained" : "outlined"}
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterList />}
                color="inherit"
                size="medium"
                sx={{
                  whiteSpace: "nowrap",
                  borderColor: showFilters ? "transparent" : "divider",
                  bgcolor: showFilters ? "action.selected" : "transparent",
                  color: showFilters ? "primary.main" : "text.primary",
                  height: 56,
                }}
              >
                {showFilters ? "Ocultar Filtros" : "Exibir Filtros"}
              </Button>
            )}

            <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
              <PrimaryButton
                width={isMobile ? "100%" : "200px"}
                height="56px"
                onClick={onSubmit}
                loading={isSearching}
                startIcon={!isMobile && <Search />}
              >
                {isMobile ? "Buscar" : isSearching ? "Buscando..." : "Buscar"}
              </PrimaryButton>
            </Box>
          </Stack>
        </Paper>

        {/* Collapse de filtros adicionais - só exibe se houver pelo menos um filtro adicional disponível */}
        {(!hiddenFilters?.clinic || !hiddenFilters?.practitioner) && (
          <Collapse in={showFilters}>
            <Card
              elevation={0}
              variant="outlined"
              sx={{ p: 3, bgcolor: "background.default", borderStyle: "dashed" }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    fontWeight="bold"
                  >
                    FILTROS ADICIONAIS
                  </Typography>
                </Grid>

                {/* Especialidade já foi selecionada na etapa anterior, não precisa exibir aqui */}
                
                {/* Campo de Clínica/Local - oculto se hiddenFilters?.clinic for true */}
                {!hiddenFilters?.clinic && (
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Business color="action" fontSize="small" />
                        <Typography variant="body2" fontWeight="medium">
                          Clínica / Local
                        </Typography>
                      </Stack>
                      <FormAutoComplete
                        name="organization"
                        control={control}
                        label="Selecione a clínica"
                        loading={false}
                        selectOptions={organizationOptions || []}
                      />
                    </Stack>
                  </Grid>
                )}

                {/* Campo de Profissional - oculto se hiddenFilters?.practitioner for true */}
                {!hiddenFilters?.practitioner && (
                  <Grid item xs={12} md={showHealthcareService ? 4 : 6}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Person color="action" fontSize="small" />
                        <Typography variant="body2" fontWeight="medium">
                          Profissional
                        </Typography>
                      </Stack>
                      <FormAutoComplete
                        name="practitioner"
                        control={control}
                        label="Selecione o profissional"
                        selectOptions={practitionerOptions || []}
                      />
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Card>
          </Collapse>
        )}
      </Stack>
    </Fade>
  );
}
